import { useJobs, type Job, type JobStatus } from "@/api";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { useDocumentTitle } from "@/components/useDocumentTitle";
import { formatNumber, timeAgo } from "@/lib/format";
import { Badge, Card, EmptyState, Mono, ProgressBar, Skeleton } from "@/ui";
import "./pages.css";

const TYPE_LABEL: Record<string, string> = {
  bulk_create: "Create channels",
  bulk_map: "Map channels",
  backfill: "Backfill history",
  test_message: "Test message",
  recreate_webhook: "Recreate webhook",
};

const STATUS_TONE: Record<JobStatus, "neutral" | "info" | "success" | "danger" | "warning"> = {
  queued: "neutral",
  running: "info",
  done: "success",
  failed: "danger",
  cancelled: "warning",
};

export function JobsPage() {
  useDocumentTitle("Jobs");
  const jobs = useJobs();
  const items = jobs.data?.items ?? [];
  const active = items.filter((j) => j.status === "queued" || j.status === "running").length;

  return (
    <div className="page">
      <PageHeader
        title="Jobs"
        description={
          active
            ? `${active} running. Long operations (creating channels, backfills) run in the background.`
            : "Long operations (creating channels, backfills) run in the background."
        }
      />
      <ErrorBanner error={jobs.error} onRetry={() => jobs.refetch()} />
      <Card padding="none">
        {jobs.isLoading ? (
          <div className="job-row">
            <Skeleton lines={4} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No jobs yet"
            description="Bulk actions from Sources show their progress here."
          />
        ) : (
          <ul className="plain-list">
            {items.map((j) => (
              <JobRow key={j.id} job={j} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function JobRow({ job }: { job: Job }) {
  const label = TYPE_LABEL[job.type] ?? job.type;
  const inFlight = job.status === "queued" || job.status === "running";
  const pct = job.total > 0 ? Math.round((job.progress / job.total) * 100) : 0;
  return (
    <li className="job-row">
      <div className="job-row__meta">
        <strong>{label}</strong>
        <span className="faint">
          started {timeAgo(job.created_at)} · <Mono value={job.id} truncate />
        </span>
      </div>
      <div className="job-row__meta">
        <ProgressBar
          label={`${label}: ${formatNumber(job.progress)} of ${formatNumber(job.total)}`}
          value={job.status === "queued" ? undefined : job.progress}
          max={Math.max(job.total, 1)}
          tone={job.status === "failed" ? "danger" : job.status === "done" ? "success" : undefined}
        />
        <span className="faint">
          {formatNumber(job.progress)} / {formatNumber(job.total)} ({pct}%)
          {job.error ? <span className="text-sm"> · {job.error}</span> : null}
          {!inFlight && job.result ? <span> · {summarise(job.result)}</span> : null}
        </span>
      </div>
      <Badge tone={STATUS_TONE[job.status]}>{job.status}</Badge>
    </li>
  );
}

function summarise(result: Record<string, unknown>): string {
  return Object.entries(result)
    .map(([k, v]) => `${k.replace(/_/g, " ")} ${String(v)}`)
    .join(", ");
}
